import { useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BOOKING_LIMIT_MESSAGE,
  BOOKING_MAX_GUESTS,
  BOOKING_MIN_GUESTS,
  deviceIsOffVenueTime,
  venueDate,
  venueIso,
  venueTime,
} from '@tg-hookah/shared';
import { useAuthStore } from '@/stores/authStore';
import { formatPhoneMask, normalisePhone } from './phone';
import { useCreateBooking } from './useCreateBooking';
import { toast } from '@/components/Toast';
import { haptic, showPopup } from '@/lib/tg';
import { Spinner } from '@/components/Spinner';
import { SmokeOverlay } from '@/components/SmokeOverlay';
import axios from 'axios';

// Local form schema (not the API schema) — separate `date` + `time` inputs,
// combined to a single ISO datetime in onSubmit per the shared `createBookingSchema`.
const formSchema = z.object({
  guestName: z.string().trim().min(2, 'Минимум 2 символа').max(80),
  phone: z
    .string()
    .trim()
    .refine((v) => v.replace(/\D/g, '').length >= 11, 'Введите полный номер'),
  date: z.string().min(1, 'Укажите дату'),
  time: z.string().min(1, 'Укажите время'),
  guestsCount: z.coerce.number().int().min(BOOKING_MIN_GUESTS).max(BOOKING_MAX_GUESTS),
  durationMin: z.coerce.number().int().min(30).max(600),
  comment: z.string().trim().max(1000).optional(),
});

type FormValues = z.infer<typeof formSchema>;

const DURATIONS = [60, 120, 180];

interface Props {
  onSuccess?: () => void;
}

export function BookingForm({ onSuccess }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const create = useCreateBooking();
  const [smokeVisible, setSmokeVisible] = useState(false);

  // The guest books a table in Moscow, so every date/time in this form is
  // Moscow wall-clock — never the device's. A guest abroad must be able to
  // type the time the venue will hold the table at.
  const today = venueDate();
  const offVenueTime = deviceIsOffVenueTime();

  // Prefill from a deep link (voice-booking sends the guest here when a required
  // field — or the phone — is missing): /booking?date=&time=&guests=&duration=
  const [params] = useSearchParams();
  const qpDate = params.get('date');
  const qpTime = params.get('time');
  const qpGuests = Number(params.get('guests'));
  const qpDuration = Number(params.get('duration'));
  const prefillDate = qpDate && /^\d{4}-\d{2}-\d{2}$/.test(qpDate) ? qpDate : today;
  const prefillTime = qpTime && /^\d{2}:\d{2}$/.test(qpTime) ? qpTime : '20:00';
  const prefillGuests =
    Number.isInteger(qpGuests) && qpGuests >= BOOKING_MIN_GUESTS && qpGuests <= BOOKING_MAX_GUESTS
      ? qpGuests
      : BOOKING_MIN_GUESTS;
  const prefillDuration = [60, 120, 180].includes(qpDuration) ? qpDuration : 120;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      guestName: user?.firstName ?? '',
      phone: user?.phone ? formatPhoneMask(user.phone) : '+7',
      date: prefillDate,
      time: prefillTime,
      guestsCount: prefillGuests,
      durationMin: prefillDuration,
      comment: '',
    },
  });

  const pickedDate = useWatch({ control, name: 'date' });
  // When the user picks today, clamp the time picker's min to the current
  // Moscow moment (HH:mm) so the native control rejects past slots outright.
  const minTime = pickedDate === today ? venueTime(Date.now() + 5 * 60_000) : undefined;

  async function onSubmit(values: FormValues) {
    // Stamp the entered wall-clock as Moscow time. Parsing it bare would use
    // the device timezone, so a guest in UTC+7 asking for 18:00 would land a
    // 14:00 Moscow booking.
    const target = new Date(venueIso(values.date, values.time));
    // Sanity check before we hit the API — server also rejects past dates
    // but returns a generic 400; this keeps the UX clear.
    if (Number.isNaN(target.getTime()) || target.getTime() <= Date.now()) {
      const msg = 'Нельзя забронировать на прошедшее время. Выберите более позднюю дату.';
      haptic('error');
      setError('time', { message: msg });
      toast(msg, 'error');
      return;
    }

    const payload = {
      guestName: values.guestName,
      phone: normalisePhone(values.phone),
      date: target.toISOString(),
      durationMin: values.durationMin,
      guestsCount: values.guestsCount,
      comment: values.comment || undefined,
    };
    try {
      await create.mutateAsync(payload);
      haptic('success');
      // Trigger the smoke ceremony — navigation happens when it finishes.
      setSmokeVisible(true);
      onSuccess?.();
    } catch (err) {
      haptic('error');
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 429) {
          toast(t('booking.rateLimit'), 'error');
          return;
        }
        // Server returns "Validation failed" for past-date/malformed input;
        // translate that into a human-readable hint in Russian.
        const raw = (err.response?.data as { message?: string } | undefined)?.message;
        // Active-bookings limit → blocking native popup (not a transient toast).
        if (err.response?.status === 400 && raw === BOOKING_LIMIT_MESSAGE) {
          showPopup(BOOKING_LIMIT_MESSAGE);
          return;
        }
        const msg =
          err.response?.status === 400 && (!raw || /validation/i.test(raw))
            ? 'Бронирование на эту дату недоступно. Выберите более позднее время.'
            : (raw ?? t('common.error'));
        setError('root', { message: msg });
        toast(msg, 'error');
        return;
      }
      toast(t('common.error'), 'error');
    }
  }

  // Bring the focused field into view once the iOS keyboard has animated up;
  // a 250ms defer lets Safari finish layout before we scroll.
  const scrollFocusedIntoView = (el: HTMLElement) => {
    window.setTimeout(() => {
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch {
        el.scrollIntoView();
      }
    }, 250);
  };

  return (
    <>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        // Block implicit submit on Enter from single-line inputs so hitting
        // "Ввод" on the iOS keyboard doesn't accidentally send the booking.
        // The explicit submit button stays the only way to submit.
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            (e.target as HTMLElement).tagName !== 'TEXTAREA' &&
            (e.target as HTMLElement).tagName !== 'BUTTON'
          ) {
            e.preventDefault();
          }
        }}
        onFocusCapture={(e) => {
          const t = e.target as HTMLElement;
          if (
            t.tagName === 'INPUT' ||
            t.tagName === 'TEXTAREA' ||
            t.tagName === 'SELECT'
          ) {
            scrollFocusedIntoView(t);
          }
        }}
      >
        <Field label={t('booking.guestName')} error={errors.guestName?.message}>
          <input
            type="text"
            autoComplete="name"
            {...register('guestName')}
            className={inputCls}
          />
        </Field>

        <Field label={t('booking.phone')} error={errors.phone?.message}>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={field.value}
                onChange={(e) => field.onChange(formatPhoneMask(e.target.value))}
                className={inputCls}
              />
            )}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('booking.date')} error={errors.date?.message}>
            <input type="date" min={today} {...register('date')} className={inputCls} />
          </Field>
          <Field label={t('booking.time')} error={errors.time?.message}>
            <input
              type="time"
              min={minTime}
              {...register('time')}
              className={inputCls}
            />
          </Field>
        </div>

        {/* Only surfaced when the device clock disagrees with Moscow — for a
            guest already in Moscow the note is noise. */}
        {offVenueTime ? (
          <p className="-mt-2 font-mono text-[10px] leading-relaxed text-white/45">
            {t('booking.moscowTimeHint', { time: venueTime() })}
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('booking.guestsCount')} error={errors.guestsCount?.message}>
            <select {...register('guestsCount')} className={inputCls}>
              {Array.from(
                { length: BOOKING_MAX_GUESTS - BOOKING_MIN_GUESTS + 1 },
                (_, i) => BOOKING_MIN_GUESTS + i,
              ).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('booking.durationMin')} error={errors.durationMin?.message}>
            <select {...register('durationMin')} className={inputCls}>
              {DURATIONS.map((d) => (
                <option key={d} value={d}>
                  {d / 60} {d / 60 === 1 ? 'час' : d / 60 < 5 ? 'часа' : 'часов'}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t('booking.comment')} error={errors.comment?.message}>
          <textarea
            rows={3}
            placeholder={t('booking.commentPlaceholder')}
            {...register('comment')}
            className={inputCls}
          />
        </Field>

        {errors.root?.message ? (
          <p role="alert" className="text-sm text-red-500">
            {errors.root.message}
          </p>
        ) : null}

        <button
          type="submit"
          data-track="click:booking-submit"
          disabled={isSubmitting || create.isPending}
          className="relative overflow-hidden rounded-full py-3.5 font-display tracking-[0.08em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-transform active:scale-[0.98]"
          style={{
            background: '#ffffff',
            color: '#0a0a0b',
            boxShadow:
              '0 10px 28px -12px rgba(255,255,255,0.35), 0 0 30px -12px rgba(194,58,46,0.35)',
          }}
        >
          {isSubmitting || create.isPending ? <Spinner className="w-4 h-4" /> : null}
          <span className="text-[13px]">{t('booking.submit')}</span>
          <span aria-hidden className="font-mono text-[12px] opacity-60">
            ↗
          </span>
        </button>
      </form>

      <SmokeOverlay
        show={smokeVisible}
        onDone={() => {
          setSmokeVisible(false);
          toast(t('booking.successTitle'), 'success');
          navigate('/profile');
        }}
        title={t('booking.successTitle')}
        subtitle={t('booking.successText')}
      />
    </>
  );
}

const inputCls =
  'w-full rounded-xl bg-white/[0.03] text-text px-3.5 py-3 outline-none border border-white/10 focus:border-white/35 focus:bg-white/[0.05] transition-colors placeholder:text-white/30';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="font-mono text-[10px] text-white/50 uppercase tracking-[0.14em]">
        {label}
      </span>
      {children}
      {error ? (
        <span role="alert" className="text-xs" style={{ color: 'var(--ember)' }}>
          {error}
        </span>
      ) : null}
    </label>
  );
}
